/* eslint-disable */
import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Ban, Car, Eye, Lock, Phone, Plus, QrCode, ShieldCheck, Undo2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { apiFetch } from "../auth/apiClient";
import { useAuth } from "../auth/AuthContext";
import { carsApi, carLabel } from "../carpools/api";
import { toast } from "../ui";
import AvatarWidget from "./AvatarWidget";
import { resolveAvatarUrl } from "../utils/avatarUrl";
import "./ProfilePage.css";

const ROLE_LABEL = {
  PRIMARY_OWNER: "Primary Owner",
  SHARED_OWNER: "Shared Owner",
  RESIDENT: "Resident",
  TENANT: "Tenant",
};

// Outside-community staff app-roles → a friendly label for the profile header.
const STAFF_ROLE_LABEL = {
  FACILITY_MANAGER: "Facility Manager",
  SECURITY_INCHARGE: "Security Incharge",
  SECURITY_GUARD: "Security Guard",
  CLUBHOUSE_SECURITY: "Clubhouse Security",
  SERVICE_STAFF: "Support Staff",
};
const OUTSIDE_STAFF_ROLES = Object.keys(STAFF_ROLE_LABEL);

function staffRoleLabel(profile) {
  const roles = profile.app_roles || [];
  for (const r of OUTSIDE_STAFF_ROLES) {
    if (roles.includes(r)) return STAFF_ROLE_LABEL[r];
  }
  return null;
}

const ADMIN_LABEL = {
  SUPER_ADMIN: "Super Admin",
  BOARD_MEMBER: "Board Member",
};

const STATUS_BADGE_CLASS = {
  APPROVED: "vsm-status-approved",
  PENDING: "vsm-status-pending",
  REJECTED: "vsm-status-rejected",
  BLOCKED: "vsm-status-rejected",
  REGISTERED: "vsm-status-pending",
  PHONE_VERIFIED: "vsm-status-pending",
  DEACTIVATED: "vsm-status-rejected",
};

function initials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function topRoleLabel(profile) {
  if (profile.is_admin) {
    return ADMIN_LABEL[profile.admin_role] || "Admin";
  }
  const primary = (profile.memberships || []).find(
    (m) => m.approval_status === "APPROVED"
  );
  if (primary) return ROLE_LABEL[primary.role] || primary.role;
  if (profile.user_type === "PRIMARY_USER") return "Primary User";
  if (profile.user_type === "SECONDARY_USER") return "Secondary User";
  return "Member";
}

export default function ProfilePage() {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [serviceHistory, setServiceHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone_number: "",
    alternative_phone_number: "",
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [pendingPhone, setPendingPhone] = useState(null);
  const [phoneSubmittedMsg, setPhoneSubmittedMsg] = useState("");
  const [revealingPhone, setRevealingPhone] = useState(false);
  const [revealError, setRevealError] = useState("");
  const [identity, setIdentity] = useState(null);
  const [blockBusy, setBlockBusy] = useState(false);
  // Acting-as-admin — block/unblock is an admin-only action and the backend
  // enforces it too; gating on the view keeps role-switched admins clean.
  const { isAdminView } = useAuth();

  const currentUserId = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u.id || null;
    } catch (_) {
      return null;
    }
  })();
  const isOwn = !userId || userId === currentUserId;

  const loadPendingPhone = useCallback(async () => {
    if (!isOwn) return;
    try {
      const data = await apiFetch("/api/users/me/phone-request", {
        authRequired: true,
      });
      setPendingPhone(data.request);
    } catch (_) {}
  }, [isOwn]);

  const load = useCallback(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("You must be logged in to view profiles.");
      setLoading(false);
      return;
    }
    setLoading(true);
    const path = userId ? `/api/users/${userId}` : "/api/users/me";
    apiFetch(path, { method: "GET", token })
      .then((d) => {
        setProfile(d);
        setForm({
          name: d.name || "",
          email: d.email || "",
          phone_number: d.phone_number || "",
          alternative_phone_number: d.alternative_phone_number || "",
        });
        setLoading(false);
        // Background-fetch service history for the Tenure section.
        // Failure is silent — viewers can still see the rest of the
        // profile if this 403s or 500s.
        const histId = userId || d.id;
        if (histId) {
          apiFetch(`/api/users/${histId}/service-history`, {
            method: "GET",
            token,
          })
            .then(setServiceHistory)
            .catch(() => {});
        }
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [userId]);

  useEffect(() => {
    load();
    loadPendingPhone();
  }, [load, loadPendingPhone]);

  // The caller's own gate-identity QR — the personal code a resident shows a
  // guard/FM to prove community membership. Only ever fetched for self.
  useEffect(() => {
    if (!isOwn) {
      setIdentity(null);
      return;
    }
    apiFetch("/api/identity/me", { authRequired: true })
      .then(setIdentity)
      .catch(() => {});
  }, [isOwn]);

  const startEdit = () => {
    setEditing(true);
    setSaveError("");
    setPhoneSubmittedMsg("");
  };

  const cancelEdit = () => {
    setEditing(false);
    setSaveError("");
    setForm({
      name: profile.name || "",
      email: profile.email || "",
      phone_number: profile.phone_number || "",
      alternative_phone_number: profile.alternative_phone_number || "",
    });
  };

  const save = async () => {
    setSaveError("");
    if (!form.name.trim()) {
      setSaveError("Name is required");
      return;
    }
    if (!/^[0-9]{10}$/.test(form.phone_number)) {
      setSaveError("Phone number must be 10 digits");
      return;
    }
    if (
      form.alternative_phone_number &&
      !/^[0-9]{10}$/.test(form.alternative_phone_number)
    ) {
      setSaveError("Alternative phone must be 10 digits");
      return;
    }
    setSaving(true);
    try {
      const updated = await apiFetch("/api/users/me", {
        method: "PATCH",
        authRequired: true,
        body: {
          name: form.name,
          email: form.email,
          phone_number: form.phone_number,
          alternative_phone_number: form.alternative_phone_number,
        },
      });
      setProfile(updated);
      try {
        const cached = JSON.parse(localStorage.getItem("user") || "{}");
        cached.name = updated.name;
        cached.phone_number = updated.phone_number;
        cached.email = updated.email;
        localStorage.setItem("user", JSON.stringify(cached));
      } catch (_) {}
      setEditing(false);
      if (updated.pending_phone_request_id) {
        setPhoneSubmittedMsg(
          "Mobile number change requested. It will take effect once approved."
        );
        await loadPendingPhone();
      } else {
        setPhoneSubmittedMsg("");
      }
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const cancelPhoneRequest = async () => {
    if (!window.confirm("Cancel your pending mobile change request?")) return;
    try {
      await apiFetch("/api/users/me/phone-request", {
        method: "DELETE",
        authRequired: true,
      });
      setPendingPhone(null);
    } catch (e) {
      alert(e.message);
    }
  };

  const isBlocked = profile?.activation_status === "BLOCKED";

  const toggleBlock = async () => {
    if (!profile?.id) return;
    let reason = null;
    if (!isBlocked) {
      reason = window.prompt(
        `Block ${profile.name || "this member"}? They will lose all app access until unblocked. Add a reason (optional):`
      );
      if (reason === null) return; // cancelled
    } else if (
      !window.confirm(`Unblock ${profile.name || "this member"} and restore their access?`)
    ) {
      return;
    }
    setBlockBusy(true);
    try {
      const r = await apiFetch(
        `/api/users/${profile.id}/${isBlocked ? "unblock" : "block"}`,
        {
          method: "POST",
          authRequired: true,
          body: isBlocked ? {} : { reason: reason.trim() || null },
        }
      );
      setProfile((p) => ({ ...p, activation_status: r.activation_status }));
      toast.success(isBlocked ? "Member unblocked" : "Member blocked");
    } catch (e) {
      toast.error(e.message || "Could not update the member");
    } finally {
      setBlockBusy(false);
    }
  };

  if (loading) return <div className="vsp-shell"><div className="vsp-empty">Loading…</div></div>;
  if (error) return <div className="vsp-shell"><div className="vsp-empty">{error}</div></div>;
  if (!profile) return null;

  const staffLabel = staffRoleLabel(profile);
  const isOutsideStaff =
    !!staffLabel && (profile.memberships || []).length === 0;
  const roleLabel = isOutsideStaff ? staffLabel : topRoleLabel(profile);
  const approvedVillaId =
    (profile.memberships || []).find((m) => m.approval_status === "APPROVED")
      ?.villa?.id || null;
  const phoneChanged =
    form.phone_number && form.phone_number !== profile.phone_number;
  const approverHint =
    profile.user_type === "PRIMARY_USER"
      ? "An admin/board member will need to approve the change."
      : "Your primary owner or an admin will need to approve the change.";

  return (
    <div className="vsp-shell">
      <div className="vsp-card">
        <div className="vsp-head">
          {isOwn ? (
            <AvatarWidget
              currentUrl={profile.avatar_url}
              name={profile.name}
              size={96}
              onChanged={(url) => {
                setProfile((p) => ({ ...p, avatar_url: url }));
                try {
                  const cached = JSON.parse(localStorage.getItem("user") || "{}");
                  cached.avatar_url = url;
                  localStorage.setItem("user", JSON.stringify(cached));
                } catch (_) {}
              }}
            />
          ) : profile.avatar_url ? (
            <div className="vsp-avatar vsp-avatar-img">
              <img src={resolveAvatarUrl(profile.avatar_url)} alt={profile.name || "avatar"} />
            </div>
          ) : (
            <div className="vsp-avatar">{initials(profile.name)}</div>
          )}
          <div className="vsp-head-text">
            <h1>{profile.name || "Unnamed user"}</h1>
            <div className="vsp-role-pill">{roleLabel}</div>
            <div className="vsp-head-meta">
              <span className={profile.phone_visible === false ? "vsp-phone-masked" : ""}>
                {profile.phone_visible === false ? (
                  <Lock size={13} strokeWidth={2.2} style={{ marginRight: 4, verticalAlign: "-2px" }} />
                ) : (
                  <Phone size={13} strokeWidth={2.2} style={{ marginRight: 4, verticalAlign: "-2px" }} />
                )}
                +91 {profile.phone_number}
              </span>
              {!isOwn && profile.phone_visible === false && (
                <button
                  type="button"
                  className="vsp-reveal-btn"
                  onClick={async () => {
                    setRevealError("");
                    setRevealingPhone(true);
                    try {
                      await apiFetch(
                        `/api/users/${profile.id}/request-phone-access`,
                        { method: "POST", authRequired: true, body: {} }
                      );
                      load();
                    } catch (e) {
                      setRevealError(e.message || "Could not reveal");
                    } finally {
                      setRevealingPhone(false);
                    }
                  }}
                  disabled={revealingPhone}
                >
                  <Eye size={13} strokeWidth={2.2} />
                  {revealingPhone ? "Revealing…" : "View phone number"}
                </button>
              )}
              {profile.email && <span>· {profile.email}</span>}
            </div>
            {revealError && (
              <div className="vsp-banner vsp-banner-error">{revealError}</div>
            )}
            {!isOwn && profile.phone_visible === false && (
              <div className="vsp-phone-note">
                Mobile numbers are masked across the community. Clicking{" "}
                <em>View phone number</em> reveals it and records an audit
                entry so {profile.name?.split(" ")[0] || "the member"} can
                see who looked it up.
              </div>
            )}
            <div className="vsp-badges">
              {profile.is_admin && (
                <span className="vsm-badge vsm-badge-admin">
                  {ADMIN_LABEL[profile.admin_role] || "Admin"}
                </span>
              )}
              <span className="vsm-badge">
                {profile.user_type === "PRIMARY_USER" ? "Primary user" : "Secondary user"}
              </span>
              <span className={`vsm-badge ${STATUS_BADGE_CLASS[profile.activation_status] || ""}`}>
                {profile.activation_status}
              </span>
            </div>
          </div>
          {isOwn && !editing && (
            <button className="vsp-edit-btn" onClick={startEdit}>
              Edit Profile
            </button>
          )}
          {!isOwn && isAdminView && !profile.is_admin && (
            <button
              className={isBlocked ? "vsp-btn-secondary" : "vsp-btn-reject"}
              onClick={toggleBlock}
              disabled={blockBusy}
              title={
                isBlocked
                  ? "Restore this member's access"
                  : "Block this member from the app"
              }
            >
              {isBlocked ? (
                <Undo2 size={14} strokeWidth={2.2} style={{ marginRight: 5, verticalAlign: "-2px" }} />
              ) : (
                <Ban size={14} strokeWidth={2.2} style={{ marginRight: 5, verticalAlign: "-2px" }} />
              )}
              {blockBusy ? "Working…" : isBlocked ? "Unblock" : "Block"}
            </button>
          )}
        </div>

        {isOwn && pendingPhone && (
          <div className="vsp-banner vsp-banner-pending">
            <div>
              <strong>Mobile change pending approval.</strong>{" "}
              You requested to change <code>+91 {pendingPhone.current_phone}</code> →{" "}
              <code>+91 {pendingPhone.requested_phone}</code>.{" "}
              <span className="vsp-banner-sub">
                {pendingPhone.approver_role_required === "ADMIN"
                  ? "Waiting on an admin/board member."
                  : "Waiting on your primary owner or an admin."}
              </span>
            </div>
            <button className="vsp-banner-btn" onClick={cancelPhoneRequest}>
              Cancel
            </button>
          </div>
        )}

        {isOwn && phoneSubmittedMsg && (
          <div className="vsp-banner vsp-banner-info">{phoneSubmittedMsg}</div>
        )}

        <div className="vsp-section">
          <h3>Contact Details</h3>
          {editing ? (
            <div className="vsp-form">
              {profile.security_role && (
                <div className="vsp-security-note">
                  Name and primary mobile are managed by your security incharge
                  and cannot be changed here.
                </div>
              )}
              <div className="vsp-field">
                <label>Full name <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  disabled={!!profile.security_role}
                />
              </div>
              <div className="vsp-field">
                <label>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                />
              </div>
              <div className="vsp-field">
                <label>Mobile number <span className="req">*</span></label>
                <div className="vsp-phone-row">
                  <span>+91</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={form.phone_number}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        phone_number: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    placeholder="10-digit primary mobile"
                    disabled={!!profile.security_role}
                  />
                </div>
                {phoneChanged && !profile.security_role && (
                  <p className="vsp-hint">
                    ⚠ Changing your mobile number creates an approval request.{" "}
                    {approverHint}
                  </p>
                )}
              </div>
              <div className="vsp-field">
                <label>Alternative mobile number</label>
                <div className="vsp-phone-row">
                  <span>+91</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={form.alternative_phone_number}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        alternative_phone_number: e.target.value.replace(
                          /\D/g,
                          ""
                        ),
                      })
                    }
                    placeholder="Optional secondary mobile"
                  />
                </div>
              </div>

              {saveError && <div className="vsp-error">{saveError}</div>}

              <div className="vsp-form-actions">
                <button
                  className="vsp-btn-secondary"
                  onClick={cancelEdit}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="vsp-btn-primary"
                  onClick={save}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>
          ) : (
            <div className="vsp-rows">
              <div><span>Name</span><strong>{profile.name || "—"}</strong></div>
              <div><span>Email</span><strong>{profile.email || "—"}</strong></div>
              <div><span>Mobile</span><strong>+91 {profile.phone_number}</strong></div>
              <div>
                <span>Alternative mobile</span>
                <strong>
                  {profile.alternative_phone_number
                    ? `+91 ${profile.alternative_phone_number}`
                    : "—"}
                </strong>
              </div>
            </div>
          )}
        </div>

        <div className="vsp-section">
          <h3>Account</h3>
          <div className="vsp-rows">
            <div><span>Role</span><strong>{roleLabel}</strong></div>
            <div><span>User type</span><strong>{profile.user_type === "PRIMARY_USER" ? "Primary user" : "Secondary user"}</strong></div>
            <div><span>Status</span><strong>{profile.activation_status}</strong></div>
            <div><span>Joined</span><strong>{fmtDate(profile.created_at)}</strong></div>
            <div><span>Last login</span><strong>{fmtDate(profile.last_login_at)}</strong></div>
            <div><span>Login count</span><strong>{profile.login_count}</strong></div>
          </div>
        </div>

        {isOwn && identity && identity.is_member && (
          <div className="vsp-section">
            <h3>My gate QR</h3>
            <div className="vsp-gateqr">
              <div className="vsp-gateqr-code">
                <QRCodeSVG value={identity.token} size={132} level="M" />
              </div>
              <div className="vsp-gateqr-text">
                <p>
                  <ShieldCheck size={14} strokeWidth={2.2} /> Show this to a
                  guard or the Facility Manager at the gate to confirm you're a
                  community member — no need to be on a visitor's pass.
                </p>
                <div className="vsp-gateqr-villas">
                  {(identity.villas || []).map((v) => (
                    <span key={v.villa_number} className="vsp-gateqr-villa">
                      {v.villa_number} · {v.role_label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {!isOutsideStaff && (
        <div className="vsp-section">
          <h3>
            Villa memberships{" "}
            <span className="vsp-section-count">
              ({profile.memberships.length})
            </span>
          </h3>
          {profile.memberships.length === 0 ? (
            <div className="vsp-empty-small">No villa memberships yet.</div>
          ) : (
            <div className="vsp-membership-list">
              {profile.memberships.map((m, i) => (
                <Link
                  key={i}
                  to={`/residents#villa-${m.villa.villa_number}`}
                  className="vsp-membership"
                >
                  <div className="vsp-membership-main">
                    <div className="vsp-membership-villa">{m.villa.villa_number}</div>
                    <div className="vsp-membership-meta">
                      <span>{ROLE_LABEL[m.role] || m.role}</span>
                      {m.relation && <span>· {m.relation}</span>}
                      {m.tenant_end_date && <span>· until {m.tenant_end_date}</span>}
                    </div>
                  </div>
                  <span className={`vsm-badge ${STATUS_BADGE_CLASS[m.approval_status] || ""}`}>
                    {m.approval_status}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
        )}

        {isOwn && !isOutsideStaff && (
          <MyCarsSection villaId={approvedVillaId} />
        )}

        {serviceHistory && serviceHistory.has_history && (
          <ServiceHistorySection history={serviceHistory} />
        )}

        <div className="vsp-actions">
          <Link to="/residents" className="vs-link">← Back to residents</Link>
        </div>
      </div>
    </div>
  );
}

function MyCarsSection({ villaId }) {
  // null = still loading; [] = loaded-and-empty.
  const [cars, setCars] = useState(null);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    make: "",
    model_name: "",
    color: "",
    plate_number: "",
    year: "",
  });
  const [addError, setAddError] = useState("");
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const load = useCallback(() => {
    setError("");
    carsApi
      .list()
      .then((d) => setCars(Array.isArray(d) ? d : []))
      .catch((e) => {
        setError(e.message || "Could not load your cars.");
        setCars([]);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () =>
    setForm({ make: "", model_name: "", color: "", plate_number: "", year: "" });

  const add = async () => {
    setAddError("");
    if (!villaId) {
      setAddError("You need to be linked to a villa before you can register cars.");
      return;
    }
    if (
      !form.make.trim() ||
      !form.model_name.trim() ||
      !form.color.trim() ||
      !form.plate_number.trim()
    ) {
      setAddError("Make, model, colour, and plate number are required.");
      return;
    }
    let year;
    if (form.year.trim()) {
      year = parseInt(form.year.trim(), 10);
      if (isNaN(year) || year < 1980 || year > 2100) {
        setAddError("Year must be between 1980 and 2100.");
        return;
      }
    }
    setAdding(true);
    try {
      await carsApi.add({
        villa_id: villaId,
        make: form.make.trim(),
        model_name: form.model_name.trim(),
        color: form.color.trim(),
        plate_number: form.plate_number.trim(),
        ...(year !== undefined ? { year } : {}),
      });
      toast.success("Car added");
      resetForm();
      setShowAdd(false);
      load();
    } catch (e) {
      setAddError(e.message || "Could not add car.");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (car) => {
    if (!window.confirm(`Remove ${carLabel(car)}?`)) return;
    setRemovingId(car.id);
    try {
      await carsApi.remove(car.id);
      toast.success("Car removed");
      setCars((prev) => (prev || []).filter((c) => c.id !== car.id));
    } catch (e) {
      toast.error(e.message || "Could not remove car.");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="vsp-section">
      <div className="vsp-section-row">
        <h3>
          <Car size={14} strokeWidth={2.2} style={{ marginRight: 6, verticalAlign: "-2px" }} />
          My Cars
          {cars ? <span className="vsp-section-count"> ({cars.length})</span> : null}
        </h3>
        {!showAdd && (
          <button
            className="vsp-villa-add-btn"
            onClick={() => {
              setAddError("");
              setShowAdd(true);
            }}
          >
            <Plus size={14} strokeWidth={2.4} /> Add car
          </button>
        )}
      </div>

      {error && <div className="vsp-error">{error}</div>}

      {cars === null ? (
        <div className="vsp-empty-small">Loading…</div>
      ) : cars.length === 0 && !showAdd ? (
        <div className="vsp-empty-small">No cars registered yet.</div>
      ) : cars.length > 0 ? (
        <div className="vsp-membership-list">
          {cars.map((car) => (
            <div key={car.id} className="vsp-membership">
              <div className="vsp-membership-main">
                <div className="vsp-membership-villa">
                  {car.color} {car.make} {car.model_name}
                  {car.year ? ` (${car.year})` : ""}
                </div>
                <div className="vsp-membership-meta">
                  <span>{car.plate_number}</span>
                </div>
              </div>
              <button
                className="vsp-btn-reject"
                onClick={() => remove(car)}
                disabled={removingId === car.id}
              >
                {removingId === car.id ? "Removing…" : "Remove"}
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {showAdd && (
        <div className="vsp-form" style={{ marginTop: 12 }}>
          <div className="vsp-field">
            <label>Make <span className="req">*</span></label>
            <input
              type="text"
              value={form.make}
              onChange={(e) => setForm({ ...form, make: e.target.value })}
              placeholder="e.g. Maruti"
            />
          </div>
          <div className="vsp-field">
            <label>Model <span className="req">*</span></label>
            <input
              type="text"
              value={form.model_name}
              onChange={(e) => setForm({ ...form, model_name: e.target.value })}
              placeholder="e.g. Swift"
            />
          </div>
          <div className="vsp-field">
            <label>Colour <span className="req">*</span></label>
            <input
              type="text"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              placeholder="e.g. White"
            />
          </div>
          <div className="vsp-field">
            <label>Plate number <span className="req">*</span></label>
            <input
              type="text"
              value={form.plate_number}
              onChange={(e) =>
                setForm({ ...form, plate_number: e.target.value.toUpperCase() })
              }
              placeholder="TS09AB1234"
            />
          </div>
          <div className="vsp-field">
            <label>Year</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={form.year}
              onChange={(e) =>
                setForm({ ...form, year: e.target.value.replace(/\D/g, "") })
              }
              placeholder="2020 (optional)"
            />
          </div>

          {!villaId && (
            <p className="vsp-hint">
              You need to be linked to a villa before you can register cars.
            </p>
          )}
          {addError && <div className="vsp-error">{addError}</div>}

          <div className="vsp-form-actions">
            <button
              className="vsp-btn-secondary"
              onClick={() => {
                setShowAdd(false);
                setAddError("");
                resetForm();
              }}
              disabled={adding}
            >
              Cancel
            </button>
            <button
              className="vsp-btn-primary"
              onClick={add}
              disabled={adding || !villaId}
            >
              {adding ? "Adding…" : "Add car"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ServiceHistorySection({ history }) {
  const fmt = (iso) =>
    iso ? new Date(iso).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : null;

  return (
    <div className="vsp-card vsp-history">
      <div className="vsp-card-h">Tenure & Service</div>
      <p className="vsp-history-sub">
        Roles this person has held — current and past — for VSMACS.
      </p>

      {history.board.length > 0 && (
        <div className="vsp-history-block">
          <div className="vsp-history-h">Board</div>
          {history.board.map((b, i) => (
            <div key={i} className="vsp-history-row">
              <div className="vsp-history-row-h">
                <strong>{b.role_label}</strong>
                {b.current && <span className="vsp-history-now">Current</span>}
              </div>
              <div className="vsp-history-meta">
                {b.term_label
                  ? b.term_label
                  : `${fmt(b.start_date) || "—"} → ${fmt(b.end_date) || "now"}`}
              </div>
              {b.responsibility && (
                <p className="vsp-history-resp">{b.responsibility}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {history.elders.length > 0 && (
        <div className="vsp-history-block">
          <div className="vsp-history-h">Elders Council</div>
          {history.elders.map((e, i) => (
            <div key={i} className="vsp-history-row">
              <div className="vsp-history-row-h">
                <strong>Elder</strong>
                {e.current && <span className="vsp-history-now">Current</span>}
              </div>
              <div className="vsp-history-meta">
                {e.term_label
                  ? e.term_label
                  : `${fmt(e.start_date) || "—"} → ${fmt(e.end_date) || "now"}`}
              </div>
              {e.bio && <p className="vsp-history-resp">{e.bio}</p>}
            </div>
          ))}
        </div>
      )}

      {history.teams.length > 0 && (
        <div className="vsp-history-block">
          <div className="vsp-history-h">Volunteer Teams</div>
          {history.teams.map((t, i) => (
            <div key={i} className="vsp-history-row">
              <div className="vsp-history-row-h">
                <strong>{t.team_name}</strong>
                {t.role === "LEAD" && (
                  <span className="vsp-history-lead">Lead</span>
                )}
                {t.current ? (
                  <span className="vsp-history-now">Active</span>
                ) : (
                  <span className="vsp-history-past">
                    {t.status === "LEFT"
                      ? "Left"
                      : t.status === "REMOVED"
                      ? "Removed"
                      : "Past"}
                  </span>
                )}
              </div>
              <div className="vsp-history-meta">
                {fmt(t.joined_at) || "—"} → {fmt(t.left_at) || "now"}
                {t.left_reason && <> · {t.left_reason}</>}
              </div>
            </div>
          ))}
        </div>
      )}

      {history.staff.length > 0 && (
        <div className="vsp-history-block">
          <div className="vsp-history-h">Staff role</div>
          {history.staff.map((s, i) => (
            <div key={i} className="vsp-history-row">
              <div className="vsp-history-row-h">
                <strong>{s.role_label}</strong>
                {s.current && <span className="vsp-history-now">Current</span>}
              </div>
              <div className="vsp-history-meta">
                {fmt(s.employed_since) || "—"} →{" "}
                {fmt(s.retired_at) || "now"}
              </div>
              {s.notes && <p className="vsp-history-resp">{s.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
